import { Environment } from '@ic-monorepo/shared-types';
import packageInfo from 'package.json';

/**
 * Compile-time flag injected via the Angular build `define` option (see project.json).
 * `true` only for the single promotable production artefact, which resolves its
 * region-specific values at runtime from `/config.json` instead of build-time
 * `fileReplacements`. All other builds keep their compiled `environment`.
 */
declare const NG_RUNTIME_CONFIG: boolean;

const CONFIG_URL = '/config.json';

const REQUIRED_KEYS: (keyof Environment)[] = ['name', 'url', 'region', 'firebase'];

const validateRuntimeConfig = (config: Partial<Environment>): void => {
  const missing = REQUIRED_KEYS.filter(key => config[key] == null);
  if (missing.length > 0) {
    throw new Error(`Invalid runtime ${CONFIG_URL}: missing required keys [${missing.join(', ')}]`);
  }
  if (!config.firebase?.projectId) {
    throw new Error(`Invalid runtime ${CONFIG_URL}: missing firebase.projectId`);
  }
};

/**
 * Resolves the application {@link Environment} before bootstrap.
 *
 * For the production artefact (`NG_RUNTIME_CONFIG`) the region-specific config is
 * fetched from `config.json` and merged with the build-time `version`, so a single
 * artefact can be promoted across regions. Every other build returns its compiled
 * `environment`. Throws on a missing or malformed config so a misconfigured deploy
 * fails fast instead of booting against the wrong project.
 *
 * @remarks The flag is read inline and the compiled environment imported lazily so
 * esbuild dead-code-eliminates it from the production bundle; the `typeof` guard
 * keeps the check safe under Jest, where the `define` is not applied. The patient
 * app uses a subset of the shared `Environment`, hence the cast.
 */
export const resolveEnvironment = async (): Promise<Environment> => {
  if (typeof NG_RUNTIME_CONFIG === 'undefined' || !NG_RUNTIME_CONFIG) {
    const { environment } = await import('../../environments/environment');
    return environment as Environment;
  }

  const response = await fetch(CONFIG_URL, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load ${CONFIG_URL}: ${response.status} ${response.statusText}`);
  }

  const config = (await response.json()) as Partial<Environment>;
  validateRuntimeConfig(config);

  return { ...(config as Environment), version: packageInfo?.version ?? 'unknown' };
};
